import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIconWrapper from '../../../components/LockIconWrapper';
import PaymentTable from './PaymentTable';
import { NumberChip } from '../../components/BookingIcons';
import { roundMoney } from '../../../../util/roundHelper';

function PaymentDialog({
  open,
  onClose,
  booking,
  paymentCountdown,
  lockPaymentBtn,
  allowMarkAsPaid = false,
  onPaymentClick,
  onMarkLineItemPaid = null,
}) {
  if (!booking || !booking.lineItems || !Array.isArray(booking.lineItems)) {
    return null;
  }

  const openLineItems = React.useMemo(() => {
    return booking.lineItems.filter(item => {
      const unpaidUnits = (item.paymentReferences || [])
        .filter(ref => !ref.isPaid && ref.paymentType === 'offline')
        .reduce((total, ref) => total + ref.count, 0);
      return unpaidUnits > 0;
    });
  }, [booking.lineItems]);

  // Warn the cashier when not every unit of the booking has to be collected
  // offline at the register: some units are already paid or were paid online,
  // so the open positions shown here are only a subset of the whole booking.
  const showPartialCollectionWarning = React.useMemo(() => {
    const activeItems = booking.lineItems.filter(
      item => !item.status || item.status === 'active'
    );
    let offlineUnpaidUnits = 0;
    let totalUnits = 0;
    activeItems.forEach(item => {
      (item.paymentReferences || []).forEach(ref => {
        totalUnits += ref.count;
        if (!ref.isPaid && ref.paymentType === 'offline') {
          offlineUnpaidUnits += ref.count;
        }
      });
    });
    return totalUnits > 0 && offlineUnpaidUnits < totalUnits;
  }, [booking.lineItems]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' spacing={2} alignItems='left'>
            <Typography variant='body1' sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
              Offene Positionen für {booking.firstName} {booking.lastName} -{' '}
              {(() => {
                const hours = Math.floor(booking.eventDurationMS / 3600000);
                const minutes = Math.floor((booking.eventDurationMS % 3600000) / 60000);
                return `${hours.toString().padStart(2, '0')}:${minutes
                  .toString()
                  .padStart(2, '0')}`;
              })()}{' '}
              Stunden
            </Typography>
            <Box display='flex' alignItems='center' justifyContent='center'>
              <NumberChip number={booking.participantCount} size='small' color='primary' />
            </Box>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {showPartialCollectionWarning && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Achtung, es müssen nicht alle Positionen kassiert werden
          </Alert>
        )}
        <PaymentTable
          lineItems={openLineItems}
          onMarkLineItemPaid={onMarkLineItemPaid}
          lockPaymentBtn={lockPaymentBtn}
        />
        <Box display='flex' justifyContent='center' alignItems='center' mt={3}>
          <Box p={3} borderRadius={2}>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold', textAlign: 'center' }}>
              <b>Summe:</b>{' '}
              {roundMoney(
                openLineItems.reduce((sum, item) => {
                  const unpaidUnits = item.paymentReferences
                    ? item.paymentReferences
                        .filter(ref => !ref.isPaid && ref.paymentType === 'offline')
                        .reduce((total, ref) => total + ref.count, 0)
                    : 0;
                  return sum + item.price * unpaidUnits;
                }, 0)
              )}
            </Typography>
            <Box display='flex' justifyContent='center' alignItems='center' mt={2}>
              <Button
                variant='contained'
                color='success'
                width='200px'
                onClick={onPaymentClick}
                disabled={lockPaymentBtn || !allowMarkAsPaid || openLineItems.length === 0}
              >
                <LockIconWrapper right={'internalBooking:update:own'}>
                  {paymentCountdown > 0
                    ? `Alles als bezahlt markieren? (${paymentCountdown}s)`
                    : 'Alles als bezahlt markieren'}
                </LockIconWrapper>
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button sx={{ color: 'red' }} onClick={onClose}>
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PaymentDialog;
